import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { JobOffer } from '../../models/job';
import { useAddCvToJob } from '../../hooks/useJobOffers';
import { FileUpload, CV_SUPPORTED_EXTENSIONS } from '../ui/FileUpload';
import { Button } from '../ui/atoms/Button';
import { X } from 'lucide-react';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { useGuardedAction } from '../../hooks/useGuardedAction';

interface AddCvModalProps {
  job: JobOffer;
  onClose: () => void;
  onUploadComplete?: (success: boolean) => void;
  onUploadStart?: (fileCount: number) => void;
}

export const AddCvModal: React.FC<AddCvModalProps> = ({ job, onClose, onUploadComplete, onUploadStart }) => {
  const { t } = useTranslation(['job', 'common']);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const addCvMutation = useAddCvToJob(job.id);

  const handleFilesSelected = (files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const { guardedAction } = useGuardedAction('cv', () => selectedFiles.length, 'upload_modal');

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      toast.error(t('job:add_cv_modal.toast_select_required'));
      return;
    }

    guardedAction(async () => {
      if (onUploadStart) {
        onUploadStart(selectedFiles.length);
      }

      try {
        const uploadMessage =
          selectedFiles.length === 1
            ? t('job:add_cv_modal.toast_uploading_one')
            : t('job:add_cv_modal.toast_uploading_other', { count: selectedFiles.length });
        const uploadToastId = toast.loading(uploadMessage);

        await addCvMutation.mutateAsync(selectedFiles);

        toast.dismiss(uploadToastId);

        if (onUploadComplete) {
          onUploadComplete(true);
        }

        onClose();
      } catch (error) {
        const errorMessage = t('job:add_cv_modal.toast_failed', {
          error: error instanceof Error ? error.message : t('job:add_cv_modal.unknown_error'),
        });
        toast.error(errorMessage);

        if (onUploadComplete) {
          onUploadComplete(false);
        }
      }
    })();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !addCvMutation.isPending) {
      onClose();
    }
  };

  const fileCount = selectedFiles.length;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      {...getGTMAttributes(createGTMTracking('UPLOAD_MODAL_ACTION', { target: 'close_backdrop', origin: 'upload_modal' }))}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h5>{t('job:add_cv_modal.title', { jobTitle: job.title })}</h5>
          <Button
            id="add-cv-modal-close-btn"
            type="icon"
            intent="muted"
            onClick={onClose}
            disabled={addCvMutation.isPending}
            icon={<X />}
            label={t('common:actions.close')}
            {...getGTMAttributes(createGTMTracking('UPLOAD_MODAL_ACTION', { target: 'close_x', origin: 'upload_modal' }))}
          />
        </div>

        <div className="mb-6">
          <FileUpload
            onFilesSelected={handleFilesSelected}
            selectedFiles={selectedFiles}
            onFileRemove={removeFile}
            acceptedFormats={CV_SUPPORTED_EXTENSIONS}
            multiple
            variant="side-by-side"
            iconColor="text-dark-invirtus"
            buttonColor="bg-dark-invirtus hover:bg-dark-invirtus/80"
            buttonTextColor="text-white"
            buttonText={t('job:add_cv_modal.upload_button')}
            description={t('job:add_cv_modal.description')}
            dragHint={t('common:file_upload.drag_hint')}
            selectedFilesTitle={t('job:add_cv_modal.selected_files', { count: fileCount })}
            id="cv-drop-area"
            showFileList
            disabled={addCvMutation.isPending}
            browseTrackingAttributes={getGTMAttributes(createGTMTracking('UPLOAD_MODAL_ACTION', { target: 'browse', origin: 'upload_modal' }))}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            id="add-cv-modal-cancel-btn"
            label={t('common:actions.cancel')}
            type="outlined"
            intent="secondary"
            onClick={onClose}
            disabled={addCvMutation.isPending}
            {...getGTMAttributes(createGTMTracking('UPLOAD_MODAL_ACTION', { target: 'close_button', origin: 'upload_modal' }))}
          />

          <Button
            id="add-cv-modal-upload-btn"
            label={
              addCvMutation.isPending
                ? t('job:add_cv_modal.uploading')
                : t('job:add_cv_modal.upload_count', { count: fileCount })
            }
            type="contained"
            intent="secondary"
            onClick={handleUpload}
            disabled={addCvMutation.isPending || fileCount === 0}
            {...getGTMAttributes(createGTMTracking('UPLOAD_MODAL_ACTION', { target: 'submit', origin: 'upload_modal' }))}
          />
        </div>
      </div>
    </div>
  );
};
